import React, { useState, useEffect } from "react";
import SEO from "../component/SEO";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { businessInfo } from "../config/business";

const STATUS_OPTIONS = ["pending", "preparing", "ready", "completed", "cancelled"];

const AdminOrders = () => {
  const user = useSelector((state) => state.user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchOrders = async (pageNum, isInitial = false) => {
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/orders?page=${pageNum}&limit=10`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Failed to fetch orders");
      else {
        const normalized = (data.orders || []).map((o) => ({
          ...o,
          deliveryStatus: (o.deliveryStatus || "pending").toLowerCase(),
          items: Array.isArray(o.items) ? o.items : [],
        }));
        setOrders((prev) => (isInitial ? normalized : [...prev, ...normalized]));
        setHasMore(data.hasMore);
      }
    } catch (err) {
      console.error("Fetch orders error:", err);
      toast.error("Server error while fetching orders");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user?.token) {
      toast.error("Please login to view orders.");
      setLoading(false);
      return;
    }
    fetchOrders(1, true);
  }, [user?.token]);

  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      setPage((prev) => {
        const nextPage = prev + 1;
        fetchOrders(nextPage);
        return nextPage;
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, loading]);

  const startEdit = (order) => {
    setEditingOrderId(order._id);
    setEditData({
      deliveryStatus: order.deliveryStatus,
      pickupDate: order.pickupDate ? order.pickupDate.split("T")[0] : "",
      pickupTime: order.pickupTime || "",
      total: order.total || 0,
    });
  };

  const handleEditChange = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const saveEdit = async (orderId) => {
    if (!user?.token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/order/${orderId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(editData),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Failed to update order");
      else {
        toast.success(data.message || "Order updated successfully");
        setEditingOrderId(null);
        setOrders((prev) =>
          prev.map((o) => (o._id === orderId ? { ...o, ...editData } : o))
        );
      }
    } catch (err) {
      console.error("Update order error:", err);
      toast.error("Server error while updating order");
    }
  };

  const filteredOrders =
    filteredStatus === "all"
      ? orders
      : orders.filter((o) => o.deliveryStatus === filteredStatus);

  if (loading)
    return <p className="text-center mt-10 text-gray-600">Loading orders...</p>;

  if (!orders.length)
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <p className="text-gray-600 text-lg">No orders found! 🛒</p>
      </div>
    );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <SEO title="Admin Orders - K Bakery" description="Manage customer orders." />
      <h1 className="text-3xl font-bold mb-6 text-center text-pink-700">
        Admin Orders
      </h1>

      {/* Filters */}
      <div className="flex gap-3 mb-6 justify-center flex-wrap">
        <button
          onClick={() => setFilteredStatus("all")}
          className={`px-4 py-1 rounded ${
            filteredStatus === "all" ? "bg-pink-600 text-white" : "bg-gray-200"
          }`}
        >
          All
        </button>
        {STATUS_OPTIONS.map((status) => (
          <button
            key={status}
            onClick={() => setFilteredStatus(status)}
            className={`px-4 py-1 rounded ${
              filteredStatus === status ? "bg-pink-600 text-white" : "bg-gray-200"
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Orders Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredOrders.map((order) => (
          <div
            key={order._id}
            className="bg-white p-4 rounded-xl shadow border border-gray-200"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold text-pink-700">
                Order #{order._id.slice(-6)}
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  {
                    pending: "bg-yellow-200 text-yellow-800",
                    preparing: "bg-orange-200 text-orange-800",
                    ready: "bg-blue-200 text-blue-800",
                    completed: "bg-green-200 text-green-800",
                    cancelled: "bg-red-200 text-red-800",
                  }[order.deliveryStatus] || "bg-gray-200 text-gray-800"
                }`}
              >
                {order.deliveryStatus}
              </span>
            </div>

            {/* Items */}
            <div className="space-y-2 mb-2">
              {order.items.length > 0 ? (
                order.items.map((item, idx) => (
                  <div className="border-b pb-1" key={idx}>
                    <div className="flex justify-between">
                      <div>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-gray-500 text-sm">
                          {item.qty} x CAD {item.price.toFixed(2)}
                        </p>
                      </div>
                      <p className="font-bold">
                        CAD {(item.qty * item.price).toFixed(2)}
                      </p>
                    </div>
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <div className="ml-2 mt-2 text-gray-700">
                        {Object.entries(item.selectedOptions).map(([title, choice]) => (
                          <p key={title} className="text-sm">
                            <span className="font-semibold">{title}:</span>{" "}
                            {Array.isArray(choice)
                              ? choice.map((c) => c.name).join(", ")
                              : choice.name}{" "}
                            {Array.isArray(choice)
                              ? choice.filter((c) => c.price > 0).map((c) => `(+CAD ${c.price})`).join(", ")
                              : choice.price > 0
                              ? `(+CAD ${choice.price})`
                              : ""}
                          </p>
                        ))}
                      </div>
                    )}
                    {item.specialInstructions && (
                      <p className="ml-2 mt-2 text-gray-500 italic text-sm">
                        Note: {item.specialInstructions}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm">No items in this order.</p>
              )}
            </div>

            {/* Delivery / Pickup Info */}
            <div className="mt-4 text-gray-600 text-sm">
              <p>
                <span className="font-semibold">Delivery Type:</span>{" "}
                {order.deliveryType}
              </p>
              <p>
                <span className="font-semibold">Delivery / Pickup Date:</span>{" "}
                {order.pickupDate
                  ? new Date(order.pickupDate).toLocaleDateString()
                  : order.deliveryDate
                  ? new Date(order.deliveryDate).toLocaleDateString()
                  : "N/A"}
              </p>
              <p>
                <span className="font-semibold">Delivery / Pickup Time:</span>{" "}
                {(() => {
                  const timeStr = order.pickupTime || order.deliveryTime;
                  if (!timeStr) return "N/A";
                  const [hourStr, minStr] = timeStr.split(":");
                  let hour = parseInt(hourStr, 10);
                  const min = minStr;
                  const ampm = hour >= 12 ? "PM" : "AM";
                  hour = hour % 12 || 12;
                  return `${hour}:${min} ${ampm}`;
                })()}
              </p>

              {order.deliveryType === "delivery" && order.deliveryAddress && (
                <div className="mt-1">
                  <p>{order.deliveryAddress.recipientName}</p>
                  <p>
                    {order.deliveryAddress.addressLine1},{" "}
                    {order.deliveryAddress.houseNumber}
                  </p>
                  <p>{order.deliveryAddress.postalCode}</p>
                </div>
              )}

              <p className="font-bold mt-2 text-gray-800">
                Total: CAD {order.total?.toFixed(2) || "0.00"}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Ordered on: {new Date(order.createdAt).toLocaleString()}
              </p>
            </div>

            {/* Maps */}
            {order.deliveryType === "pickup" && (
              <div className="mt-4">
                <h3 className="font-semibold text-lg text-pink-700 mb-2">
                  Pickup Location
                </h3>
                <iframe
                  src={businessInfo.mapEmbedUrl}
                  className="w-full h-32 rounded-xl border"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            )}

            {order.deliveryType === "delivery" &&
              order.deliveryAddress?.location && (
                <div className="mt-4">
                  <iframe
                    src={`https://maps.google.com/maps?q=${order.deliveryAddress.location.lat},${order.deliveryAddress.location.lng}&z=15&output=embed`}
                    className="w-full h-32 rounded-xl border"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              )}

            {/* Edit Button / Form */}
            {editingOrderId === order._id ? (
              <div className="space-y-2 mt-2">
                <div>
                  <label className="text-sm font-semibold">Status:</label>
                  <select
                    value={editData.deliveryStatus}
                    onChange={(e) => handleEditChange("deliveryStatus", e.target.value)}
                    className="mt-1 w-full border px-2 py-1 rounded"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold">Pickup Date:</label>
                  <input
                    type="date"
                    value={editData.pickupDate}
                    onChange={(e) => handleEditChange("pickupDate", e.target.value)}
                    className="mt-1 w-full border px-2 py-1 rounded"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Pickup Time:</label>
                  <input
                    type="time"
                    value={editData.pickupTime}
                    onChange={(e) => handleEditChange("pickupTime", e.target.value)}
                    className="mt-1 w-full border px-2 py-1 rounded"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Total:</label>
                  <input
                    type="number"
                    value={editData.total}
                    onChange={(e) => handleEditChange("total", e.target.value)}
                    className="mt-1 w-full border px-2 py-1 rounded"
                  />
                </div>
                <button
                  onClick={() => saveEdit(order._id)}
                  className="bg-pink-600 text-white px-4 py-1 rounded mt-2"
                >
                  Save
                </button>
              </div>
            ) : (
              order.deliveryStatus !== "completed" &&
              order.deliveryStatus !== "cancelled" && (
                <button
                  onClick={() => startEdit(order)}
                  className="mt-2 bg-blue-500 text-white px-3 py-1 rounded"
                >
                  Edit
                </button>
              )
            )}
            <div className="mt-3 text-sm text-gray-600">
              <p>
                User: {order.userId?.firstName} {order.userId?.lastName} (
                {order.userId?.email})
              </p>
              <p>Total: CAD {order.total?.toFixed(2) || "0.00"}</p>
              <p>Ordered on: {new Date(order.createdAt).toLocaleString()}</p>
            </div>
            {order.userId?.phoneNumber && (
  <a
    href={`https://wa.me/+1${order.userId.phoneNumber.replace(/\D/g, "")}?text=${encodeURIComponent(
      `Hello ${order.userId.firstName},\n\n` +
      `Thank you for choosing K Bakery! We have received your order and would like to confirm the details below:\n\n` +
      `Order #${order._id.slice(-6)}\n` +
      `Items:\n${order.items
        .map(
          (item) =>
            `- ${item.name} x ${item.qty} = CAD ${(item.qty * item.price).toFixed(
              2
            )}`
        )
        .join("\n")}\n` +
      `Total Amount: CAD ${order.total.toFixed(2)}\n` +
      (order.pickupDate || order.deliveryDate
        ? `Delivery/Pickup Date: ${order.pickupDate ? new Date(order.pickupDate).toLocaleDateString() : new Date(order.deliveryDate).toLocaleDateString()}\n` 
        : '') +
      (order.pickupTime || order.deliveryTime
        ? `Delivery/Pickup Time: ${order.pickupTime || order.deliveryTime}\n`
        : '') +
      (order.deliveryType === "delivery" && order.deliveryAddress
        ? `Delivery Address: ${order.deliveryAddress.recipientName}, ${order.deliveryAddress.addressLine1}, ${order.deliveryAddress.houseNumber}, ${order.deliveryAddress.postalCode}\n`
        : '') +
      `\nKindly make a half deposit via Interac to the following number: 4033603664\n` +
      `Once done, please send a screenshot of the transaction to confirm your order.\n\n` +
      `We appreciate your trust in K Bakery and look forward to serving you! \n\nThank you and have a wonderful day!`
    )}`}
    target="_blank"
    rel="noopener noreferrer"
    className="mt-2 w-full inline-block text-center bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
  >
    Get Confirmation on WhatsApp
  </a>
)}
          </div>
          
        ))}
      </div>
      {loadingMore && (
        <p className="text-center mt-6 text-gray-500 animate-pulse">
          Loading more orders...
        </p>
      )}
    </div>
  );
};

export default AdminOrders;
